import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import React, { useState } from 'react';

import {
  changedFields,
  diffPublishedPolicies,
  historyRows,
  publisherLabel,
} from './monitoring-publication-history-display';
import type { PublishedPolicy } from './monitoring-publication-history-display';

import Button from '~/client/components/base/button';
import { Drawer } from '~/client/components/base/drawer';
import StatusPill from '~/client/components/base/status-pill';
import Text from '~/client/components/base/text';
import * as GQL from '~/graphql/generated/client';

const HISTORY_LIMIT = 50;

function triggerLine(policy: PublishedPolicy): string | null {
  const parts = [
    policy.intents.length ? `Intents: ${policy.intents.join(', ')}` : null,
    policy.topics.length ? `Topics: ${policy.topics.join(', ')}` : null,
    policy.actionClasses.length
      ? `Action classes: ${policy.actionClasses.join(', ')}`
      : null,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

const PolicyItem: React.FC<{ policy: PublishedPolicy; note?: string }> = ({
  policy,
  note,
}) => {
  const meta = [
    policy.guidanceKind ? `Guidance: ${policy.guidanceKind}` : null,
    policy.severity ? `Severity: ${policy.severity}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  const triggers = triggerLine(policy);
  return (
    <Box component="li" sx={{ mb: 1.5 }}>
      <Text sz="bold-body14">{policy.title ?? policy.identifier}</Text>
      <Text sz="body12" color="secondary">
        {policy.identifier}
      </Text>
      {note && (
        <Text sz="body12" color="secondary">
          {note}
        </Text>
      )}
      {meta && <Text sz="body14">{meta}</Text>}
      {policy.guidanceSummary && (
        <Text sz="body14" color="secondary">
          {policy.guidanceSummary}
        </Text>
      )}
      {triggers && (
        <Text sz="body12" color="secondary">
          {triggers}
        </Text>
      )}
    </Box>
  );
};

const PolicyGroup: React.FC<{
  heading: string;
  policies: PublishedPolicy[];
  notes?: string[];
}> = ({ heading, policies, notes }) => (
  <Box sx={{ mb: 2 }}>
    <Text sz="bold-body16">{`${heading} (${policies.length})`}</Text>
    {policies.length ? (
      <Box component="ul" sx={{ m: 0, mt: 1, pl: 2 }}>
        {policies.map((policy, index) => (
          <PolicyItem
            key={policy.identifier}
            policy={policy}
            note={notes?.[index]}
          />
        ))}
      </Box>
    ) : (
      <Text sz="body14" color="secondary">
        None
      </Text>
    )}
  </Box>
);

interface VersionDrawerProps {
  projectId: string;
  publicationId: string;
  version: string;
  activeId?: string;
  onClose: () => void;
}

// Opens over the version list; comparison is offered only for versions other than the active one.
const VersionDrawer: React.FC<VersionDrawerProps> = ({
  projectId,
  publicationId,
  version,
  activeId,
  onClose,
}) => {
  const [comparing, setComparing] = useState(false);
  const selected = GQL.useGetGuardianPublishedBundleQuery({
    variables: { projectId, publicationId },
  });
  const active = GQL.useGetGuardianPublishedBundleQuery({
    variables: { projectId, publicationId: activeId ?? '' },
    skip: !activeId || !comparing,
  });
  const bundle = selected.data?.guardianPublishedBundle;
  const activeBundle = active.data?.guardianPublishedBundle;
  const canCompare = Boolean(activeId) && activeId !== publicationId;

  const title = comparing
    ? `Version ${version} compared with active ${
        activeBundle?.bundleVersion ?? ''
      }`
    : `Version ${version}`;

  let body: React.ReactNode;
  const error = selected.error ?? active.error;
  if (selected.loading || active.loading) {
    body = <Text sz="body14">Loading…</Text>;
  } else if (error) {
    body = (
      <Text sz="body14" color="error">
        {`Could not load this version: ${error.message}`}
      </Text>
    );
  } else if (!bundle || (comparing && !activeBundle)) {
    body = (
      <Text sz="body14" color="secondary">
        This publication carried no bundle.
      </Text>
    );
  } else if (
    !bundle.contentAvailable ||
    (comparing && !activeBundle?.contentAvailable)
  ) {
    body = (
      <Text sz="body14" color="secondary">
        The contents of this version were not kept, because it was published
        before version history existed. Versions published from now on can be
        viewed and compared.
      </Text>
    );
  } else if (comparing && activeBundle) {
    const diff = diffPublishedPolicies(bundle.policies, activeBundle.policies);
    body = (
      <>
        <Text sz="body14" color="secondary" sx={{ mb: 2 }}>
          {`What changed from version ${bundle.bundleVersion} to the active version ${activeBundle.bundleVersion}. ${diff.unchangedCount} unchanged.`}
        </Text>
        <PolicyGroup heading="Added" policies={diff.added} />
        <PolicyGroup heading="Removed" policies={diff.removed} />
        <PolicyGroup
          heading="Changed"
          policies={diff.changed.map((c) => c.after)}
          notes={diff.changed.map(
            (c) => `Changed: ${changedFields(c).join(', ')}`
          )}
        />
      </>
    );
  } else {
    body = <PolicyGroup heading="Policies" policies={bundle.policies} />;
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={title}
      width={640}
      actionButtons={
        canCompare ? (
          <Button outlined size="small" onClick={() => setComparing((v) => !v)}>
            {comparing ? 'Show policies' : 'Compare with active'}
          </Button>
        ) : undefined
      }
    >
      <Box sx={{ p: 2 }}>{body}</Box>
    </Drawer>
  );
};

export interface MonitoringPublicationHistoryProps {
  projectId: string;
}

const MonitoringPublicationHistory: React.FC<
  MonitoringPublicationHistoryProps
> = ({ projectId }) => {
  const [listOpen, setListOpen] = useState(false);
  const [selected, setSelected] = useState<{
    publicationId: string;
    version: string;
  } | null>(null);
  const { data, error, loading } = GQL.useGetGuardianPublicationsQuery({
    variables: { projectId, limit: HISTORY_LIMIT },
    skip: !projectId || !listOpen,
    fetchPolicy: 'cache-and-network',
  });

  // Same access rule as the publish panel: without Witness management access the history stays hidden.
  if (error || !projectId) {
    return null;
  }

  const rows = historyRows(data?.guardianPublications ?? []);
  const activeId = rows.find((r) => r.isActive)?.publication._id;

  return (
    <Box sx={{ mb: 2 }}>
      <Button tertiary size="small" onClick={() => setListOpen(true)}>
        Show version history
      </Button>
      <Drawer
        open={listOpen}
        onClose={() => {
          setSelected(null);
          setListOpen(false);
        }}
        title="Version history"
        width={560}
      >
        <Box sx={{ p: 2 }}>
          <Text sz="body12" color="secondary">
            Active is the version Fairly last published to Witness. Select a
            version to see its policies.
          </Text>
          {loading && !data && <Text sz="body14">Loading…</Text>}
          {!loading && !rows.length && (
            <Text sz="body14" color="secondary">
              No published versions yet.
            </Text>
          )}
          {rows.length > 0 && (
            <List disablePadding sx={{ mt: 1 }}>
              {rows.map(({ publication, isActive }) => (
                <ListItemButton
                  key={publication._id}
                  selected={selected?.publicationId === publication._id}
                  onClick={() =>
                    setSelected({
                      publicationId: publication._id,
                      version: publication.bundleVersion ?? '—',
                    })
                  }
                  sx={{
                    display: 'block',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Text sz="bold-body14">
                      {`Version ${publication.bundleVersion ?? '—'}`}
                    </Text>
                    {isActive && <StatusPill tone="success" label="Active" />}
                  </Box>
                  <Text sz="body14" color="secondary">
                    {[
                      publication.createdAt
                        ? new Date(publication.createdAt).toLocaleString()
                        : null,
                      publisherLabel(publication.requestedBy),
                      `${publication.controlCount} controls`,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </Text>
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
      {selected && (
        <VersionDrawer
          key={selected.publicationId}
          projectId={projectId}
          publicationId={selected.publicationId}
          version={selected.version}
          activeId={activeId}
          onClose={() => setSelected(null)}
        />
      )}
    </Box>
  );
};

export default MonitoringPublicationHistory;
